import { FirmwareType, Role } from '@openapi';
import { FilterFunc, DefaultOptionType } from 'rc-select/lib/Select';
import { GlobalSettings, User } from './types';

export const validatePassword = (pass: string, minLength = 16) => {
  const permittedSpecialChars = /[@$!%*?&#]/;
  if (!pass) {
    return null;
  }

  if (pass.length < minLength) {
    return `Password must contain at least ${minLength} characters`;
  }

  if (pass.length > 255) {
    return 'Password must contain less than 256 characters';
  }

  if (!permittedSpecialChars.test(pass)) {
    return 'Password must contain at least 1 special character from the permitted list: @, $, !, %, *, ?, &, #';
  }

  if (!/[A-Z]/g.test(pass)) {
    return 'Password must contain at least 1 upper letter';
  }

  if (!/[a-z]/g.test(pass)) {
    return 'Password must contain at least 1 lower letter';
  }

  return null;
};

export const validateDigitsAndLetters = (_: any, value: string) => {
  return /^[a-zA-Z0-9а-яА-Я]*$/.test(value)
    ? Promise.resolve()
    : Promise.reject(new Error('Please enter letters and digits only'));
};

export const getRandomInt = (min: number, max: number) => {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min)) + min;
};

export const getRandomBool = () => !!getRandomInt(0, 2);

export const selectSearchFilter: FilterFunc<DefaultOptionType> = (input, option) => {
  return (
    option?.props.value?.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
    option?.props.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
  );
};

export const getUserRoleByRoles = (userRoles: User['roles']) => {
  if (userRoles.includes(Role.Superadmin)) {
    return Role.Superadmin;
  }
  if (userRoles.includes(Role.Integration)) {
    return Role.Integration;
  }
  if (userRoles.includes(Role.Quality)) {
    return Role.Quality;
  }
  if (userRoles.includes(Role.Partner)) {
    return Role.Partner;
  }
};

export const isUserLocked = (user: User) => {
  return user.isLocked || user.isAuthenticationLocked;
};

export const getDiff = (start: Date, end = new Date()) => {
  const diff = new Date(end.getTime() - start.getTime());

  return dateToString(diff);
};

export const dateToString = (date: Date) => {
  const year = date.getUTCFullYear() - 1970;
  const month = date.getUTCMonth();
  const day = date.getUTCDate() - 1;
  const hour = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();
  let result = '';
  if (year > 0) {
    result += `${year} year${year > 1 ? 's' : ''}`;
  }
  if (month > 0) {
    result += ` ${month} month${month > 1 ? 's' : ''}`;
  }
  if (day > 0) {
    result += ` ${day} day${day > 1 ? 's' : ''}`;
  }
  if (hour - 3 > 0) {
    result += ` ${hour - 3} hour${hour - 3 > 1 ? 's' : ''}`;
  }
  if (minutes > 0) {
    result += ` ${minutes} minute${minutes > 1 ? 's' : ''}`;
  }
  if (seconds > 0) {
    result += ` ${seconds} second${seconds > 1 ? 's' : ''}`;
  }

  return result;
};

export const downloadFile = async (blob: Blob, name: string) => {
  // Convert your blob into a Blob URL (a special url that points to an object in the browser's memory)
  const blobUrl = URL.createObjectURL(blob);

  // Create a link element
  const link = document.createElement('a');
  link.style.display = 'none';

  // Set link's href to point to the Blob URL
  link.href = blobUrl;
  link.download = name;

  // Append link to the body
  document.body.appendChild(link);

  // Dispatch click event on the link
  // This is necessary as link.click() does not work on the latest firefox
  link.dispatchEvent(
    new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window,
    }),
  );

  // Remove link from body
  document.body.removeChild(link);
};

export const getLabelByFirmwareType = (type: FirmwareType, settings: GlobalSettings | null): string => {
  if (!settings) {
    return type;
  }

  if (type === FirmwareType.Fw) {
    return settings.firmwareLabelName;
  }

  if (type === FirmwareType.Ui) {
    return settings.uiLabelName;
  }

  if (type === FirmwareType.Loader) {
    return settings.loaderLabelName;
  }

  return type;
};
